import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from 'react-query'
import { useAuth } from '@/contexts/AuthContext'
import { systemIssuesService } from '../../../services/systemIssuesService'
import type {
  SystemIssue,
  SystemIssuePriority,
  SystemIssueStatus,
} from '../types/system-issues'

export const myIssuesKey = ['system-issues', 'mine'] as const
/** Aninhada em `myIssuesKey` de propósito: a lista da empresa contém as
 * minhas, e todo `invalidateQueries(myIssuesKey)` (match por prefixo) já
 * atualiza as duas. */
export const companyIssuesKey = [...myIssuesKey, 'company'] as const
export const allIssuesKey = (
  status?: SystemIssueStatus,
  assignedToId?: number,
  companyId?: number,
) =>
  [
    'system-issues',
    'all',
    status ?? 'any',
    assignedToId ?? 'any',
    companyId ?? 'any',
  ] as const
export const issueKey = (issueId: number) =>
  ['system-issues', 'issue', issueId] as const
export const processIssuesKey = (processId: number) =>
  ['system-issues', 'by-process', processId] as const
export const assignableDevelopersKey = ['system-issues', 'developers'] as const
export const issueMessagesKey = (issueId: number) =>
  ['system-issues', 'messages', issueId] as const

/**
 * Depois de mudar a issue (status, prioridade, reabertura): detalhe em cache,
 * listas e a conversa — toda mudança gera mensagem de sistema no chat.
 */
function refreshAfterIssueChange(
  qc: QueryClient,
  issueId: number,
  updated: SystemIssue,
) {
  qc.setQueryData(issueKey(issueId), updated)
  refreshIssueLists(qc)
  qc.invalidateQueries(issueMessagesKey(issueId))
}

/** Todas as listas de chamados: a fila (dev), os meus/da empresa e as abas
 * HUB dos processos. */
export function refreshIssueLists(qc: QueryClient) {
  qc.invalidateQueries(['system-issues', 'all'])
  qc.invalidateQueries(myIssuesKey)
  qc.invalidateQueries(['system-issues', 'by-process'])
}

/** Reportes do próprio usuário — exige "Reportar problemas do Hub". */
export function useMyIssues(enabled = true) {
  return useQuery(myIssuesKey, () => systemIssuesService.listMine(), {
    enabled,
    staleTime: 15 * 1000,
  })
}

/** Tela "Atendimento HUB" de quem não é dev: tudo que colegas da mesma
 * empresa reportaram + os meus. */
export function useCompanyIssues(enabled = true) {
  return useQuery(companyIssuesKey, () => systemIssuesService.listCompany(), {
    enabled,
    staleTime: 15 * 1000,
  })
}

/** Fila de triagem — só chamar com `enabled` gated por `capabilities.canManageDevTools`. */
export function useAllIssues(
  opts: {
    status?: SystemIssueStatus
    assignedToId?: number
    companyId?: number
    enabled?: boolean
  } = {},
) {
  const { status, assignedToId, companyId, enabled = true } = opts
  return useQuery(
    allIssuesKey(status, assignedToId, companyId),
    () => systemIssuesService.listAll({ status, assignedToId, companyId }),
    { enabled, staleTime: 15 * 1000 },
  )
}

/**
 * Problemas do Hub vinculados a um processo — alimenta a aba "HUB" dentro de
 * Ocorrências. Só faz sentido para usuário interno (o backend recusa
 * externo), por isso o `enabled`.
 */
export function useProcessIssues(processId: number | null, enabled = true) {
  return useQuery(
    processId
      ? processIssuesKey(processId)
      : ['system-issues', 'by-process', 'idle'],
    () => systemIssuesService.listByProcess(processId as number),
    { enabled: !!processId && enabled, staleTime: 15 * 1000 },
  )
}

export function useIssue(issueId: number | null) {
  return useQuery(
    issueId ? issueKey(issueId) : ['system-issues', 'issue', 'idle'],
    () => systemIssuesService.getIssue(issueId as number),
    // Id inexistente/fora do acesso: uma nova tentativa só, para a tela
    // dizer logo que não achou.
    { enabled: !!issueId, retry: 1 },
  )
}

/** Dropdown "Atribuir a" — só chamar com `enabled` gated por `capabilities.canManageDevTools`. */
export function useAssignableDevelopers(enabled = true) {
  return useQuery(
    assignableDevelopersKey,
    () => systemIssuesService.listAssignableDevelopers(),
    { enabled, staleTime: 60 * 1000 },
  )
}

export function useCreateIssue() {
  const qc = useQueryClient()
  return useMutation(
    (payload: Parameters<typeof systemIssuesService.createIssue>[0]) =>
      systemIssuesService.createIssue(payload),
    {
      onSuccess: () => {
        refreshIssueLists(qc)
      },
    },
  )
}

/** Dev-only (backend enforça). */
export function useUpdateIssueStatus(issueId: number) {
  const qc = useQueryClient()
  return useMutation(
    (status: SystemIssueStatus) =>
      systemIssuesService.updateStatus(issueId, status),
    { onSuccess: (updated) => refreshAfterIssueChange(qc, issueId, updated) },
  )
}

/** Reabre um chamado resolvido — quem vê o chamado, não só dev. */
export function useReopenIssue(issueId: number) {
  const qc = useQueryClient()
  return useMutation(() => systemIssuesService.reopen(issueId), {
    onSuccess: (updated) => refreshAfterIssueChange(qc, issueId, updated),
  })
}

/** Dev-only (backend enforça). Muda a prioridade DEPOIS de criada — na
 * criação, quem pode pedir Alta/Urgente é mais amplo (ver create form). */
export function useUpdateIssuePriority(issueId: number) {
  const qc = useQueryClient()
  return useMutation(
    (priority: SystemIssuePriority) =>
      systemIssuesService.updatePriority(issueId, priority),
    { onSuccess: (updated) => refreshAfterIssueChange(qc, issueId, updated) },
  )
}

/** Dev-only (backend enforça). Aceita se atribuir ou atribuir a outro dev. */
export function useAssignIssue(issueId: number) {
  const qc = useQueryClient()
  return useMutation(
    (assignedToId: number) =>
      systemIssuesService.assign(issueId, assignedToId),
    { onSuccess: (updated) => refreshAfterIssueChange(qc, issueId, updated) },
  )
}

/**
 * Zera o `unread_count` da issue pra quem chamou — mesmo padrão de
 * `useMarkProcessRead` em Ocorrências. Invalida as duas listas (a badge
 * some tanto da lista do reporter quanto da fila de triagem, dependendo de
 * quem marcou).
 */
export function useMarkIssueRead() {
  const qc = useQueryClient()
  return useMutation(
    (issueId: number) => systemIssuesService.markRead(issueId),
    {
      onSuccess: () => {
        refreshIssueLists(qc)
      },
    },
  )
}

/** True se alguma das MINHAS issues tem mensagem não lida — alimenta a
 * bolinha vermelha do item "Relatar um problema" na Sidebar. */
export function useHasUnreadReportedIssues(): boolean {
  // A bolinha fica na Sidebar de todo mundo: sem a permissão a consulta só
  // devolveria 403 a cada abertura de página.
  const canReport = useAuth().hasCap('can_report_system_issues')
  const { data } = useMyIssues(canReport)
  return (data ?? []).some((i) => (i.unread_count ?? 0) > 0)
}

/** Mesma ideia, só que sobre a fila de triagem inteira — alimenta a bolinha
 * de "Problemas Reportados" (dev-only, por isso o `enabled`). */
export function useHasUnreadTriageIssues(enabled: boolean): boolean {
  const { data } = useAllIssues({ enabled })
  return (data ?? []).some((i) => (i.unread_count ?? 0) > 0)
}
